/**
 * Member invitations and CSV import. Someone with an account is added straight away (accepted); someone without one is
 * emailed a link to set a password (emailed) and is added on first sign-in (accepted). pending = saved but not emailed
 * (email not set up, or the CSV import chose not to send); expired = the emailed link ran out (resend to get a new one).
 */
import { formatInstantGB } from './gbDate';
export type InvitationStatus = 'pending' | 'emailed' | 'accepted' | 'expired' | 'revoked';
export const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  pending: 'Not emailed', emailed: 'Emailed', accepted: 'Accepted', expired: 'Link expired', revoked: 'Revoked',
};

export interface InvitationInput {
  email: string;
  display_name?: string | null;
  /** Any workspace role (not a membership-derived resource role). Omit for the workspace default role. */
  role_id?: string | null;
  role_scope?: 'workspace' | 'buildings';
  location_ids?: string[];
  position_ids?: string[];
  job_title?: string | null;
}

export interface MemberInvitation extends Required<Pick<InvitationInput, 'email'>> {
  id: string;
  display_name: string | null;
  role_id: string | null;
  role_name: string | null;
  role_scope: 'workspace' | 'buildings';
  location_ids: string[];
  position_ids: string[];
  job_title: string | null;
  status: InvitationStatus;
  user_id: string | null;
  source: 'invite' | 'csv';
  invited_by: string;
  invited_by_name: string | null;
  created_at: string;
  /** When they were added (accepted). */
  added_at: string | null;
  expires_at: string | null;
  last_sent_at: string | null;
  send_count: number;
  /** The last delivery problem, e.g. a provider error. */
  last_error: string | null;
  /** A shareable link is stored for this open invitation (Copy invite link). False: Regenerate to get one. */
  has_link: boolean;
  link_created_at: string | null;
}

/**
 * An invitation's sign-up link, for an admin to copy or share. Only returned by the audited link endpoints and right
 * after creating or regenerating; single-use and expiring. Never log it or put it in another page's URL.
 */
export interface InviteLink { url: string; expires_at: string; /** Who it's for (name, else email), for the warning. */ for_name: string; email: string }
/** What the admin did with a link; each is audit-logged. */
export type InviteLinkEvent = 'copied' | 'shared';
export const inviteLinkWarning = (name: string) => `Anyone with this link can set up this account. Share it only with ${name}.`;
/** "Expires 27 Oct 2026" (UK time). */
export function inviteLinkExpiry(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (d.getTime() <= now.getTime()) return 'Expired';
  const date = formatInstantGB(d, 'd LLL yyyy');
  return `Expires ${date}`;
}
/** The exact expiry for a hover bubble: "27 Oct 2026, 10:42 UK time". */
export function inviteLinkExpiryExact(iso: string): string {
  const d = new Date(iso);
  return `${formatInstantGB(d, 'd LLL yyyy, HH:mm')} UK time`;
}

/** added = had an account and is in now; emailed = invite email sent; pending = saved without an email. */
export type InviteOutcome = 'added' | 'emailed' | 'pending' | 'already_member' | 'error';
export interface InviteResult { email: string; outcome: InviteOutcome; message?: string; invitation_id?: string; user_id?: string; /** Single invites only: the link to copy or share. Absent when it couldn't be made. */ link?: InviteLink }

export const MAX_CSV_INVITES = 200;
export const MEMBER_CSV_HEADERS = ['email', 'name', 'role', 'buildings', 'primary_building', 'positions', 'job_title'] as const;
export const MEMBER_CSV_TEMPLATE = `${MEMBER_CSV_HEADERS.join(',')}\nalex.smith@example.com,Alex Smith,Employee,Main Warehouse;Outlet Store,Main Warehouse,Picker;Forklift,Warehouse operative\n`;

/** RFC 4180-ish CSV: quoted fields, doubled quotes, CRLF or LF, BOM. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], field = '', q = false;
  const s = text.replace(/^\uFEFF/, '');
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') { if (s[i + 1] === '"') { field += '"'; i++; } else q = false; }
      else field += c;
    } else if (c === '"' && field === '') q = true;
    else if (c === ',') { row.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && s[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some(x => x.trim() !== '')) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some(x => x.trim() !== '')) rows.push(row);
  return rows;
}

export interface CsvLookups {
  roles: { id: string; name: string }[];
  locations: { id: string; name: string; code?: string | null }[];
  positions: { id: string; name: string }[];
}
export interface CsvInviteRow { line: number; input: InvitationInput; errors: string[] }

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const norm = (s: string) => s.trim().toLowerCase();
const HEADER_ALIASES: Record<string, (typeof MEMBER_CSV_HEADERS)[number]> = {
  email: 'email', 'e-mail': 'email', 'email address': 'email', name: 'name', 'display name': 'name', 'full name': 'name',
  role: 'role', buildings: 'buildings', building: 'buildings', sites: 'buildings', site: 'buildings', locations: 'buildings', 'works at': 'buildings',
  primary: 'primary_building', primary_building: 'primary_building', 'primary building': 'primary_building', 'primary location': 'primary_building', primary_location: 'primary_building', 'primary site': 'primary_building',
  positions: 'positions', position: 'positions', 'job title': 'job_title', job_title: 'job_title', title: 'job_title',
};

/**
 * Turns an uploaded CSV into invitation rows with readable per-row problems. Names are matched case-insensitively
 * (buildings also by code); lists are separated by semicolons or "|". Duplicate emails in the file are flagged.
 */
export function csvToInvitations(text: string, lk: CsvLookups): { rows: CsvInviteRow[]; problems: string[] } {
  const all = parseCsv(text);
  if (!all.length) return { rows: [], problems: ['The file is empty'] };
  const header = all[0].map(h => HEADER_ALIASES[norm(h)] ?? null);
  if (!header.includes('email')) return { rows: [], problems: ['The first row must be a header with at least an "email" column'] };
  const problems: string[] = [];
  const data = all.slice(1);
  if (data.length > MAX_CSV_INVITES) problems.push(`Only the first ${MAX_CSV_INVITES} rows can be imported at once`);
  const byName = <T extends { id: string; name: string }>(list: T[], extra?: (x: T) => string | null | undefined) => {
    const m = new Map<string, string>();
    for (const x of list) { m.set(norm(x.name), x.id); const e = extra?.(x); if (e) m.set(norm(e), x.id); }
    return m;
  };
  const roles = byName(lk.roles), locs = byName(lk.locations, l => l.code), pos = byName(lk.positions);
  const seen = new Map<string, number>();
  const rows = data.slice(0, MAX_CSV_INVITES).map((cells, i): CsvInviteRow => {
    const line = i + 2;
    const get = (k: (typeof MEMBER_CSV_HEADERS)[number]) => { const j = header.indexOf(k); return j < 0 ? '' : (cells[j] ?? '').trim(); };
    const list = (v: string) => v.split(/[;|]/).map(x => x.trim()).filter(Boolean);
    const errors: string[] = [];
    const email = get('email').toLowerCase();
    if (!EMAIL.test(email)) errors.push(email ? `"${email}" isn't an email address` : 'Email is missing');
    else if (seen.has(email)) errors.push(`Same email as row ${seen.get(email)}`);
    else seen.set(email, line);
    const roleName = get('role');
    const role_id = roleName ? roles.get(norm(roleName)) ?? null : null;
    if (roleName && !role_id) errors.push(`Unknown role "${roleName}"`);
    const location_ids: string[] = [];
    for (const b of list(get('buildings'))) { const id = locs.get(norm(b)); if (id) location_ids.push(id); else errors.push(`Unknown building "${b}"`); }
    // "primary_building" (or "primary"): the starred one. It's added to the buildings if missing and goes first, because
    // an invitation's first building is the primary. Without the column the first building listed is the primary.
    const primaryName = get('primary_building');
    if (primaryName) {
      const id = locs.get(norm(primaryName));
      if (!id) errors.push(`Unknown primary building "${primaryName}"`);
      else location_ids.splice(0, location_ids.length, id, ...location_ids.filter(x => x !== id));
    }
    const position_ids: string[] = [];
    for (const p of list(get('positions'))) { const id = pos.get(norm(p)); if (id) position_ids.push(id); else errors.push(`Unknown position "${p}"`); }
    const name = get('name'), title = get('job_title');
    if (name.length > 120) errors.push('Name is longer than 120 characters');
    if (title.length > 120) errors.push('Job title is longer than 120 characters');
    return { line, errors, input: {
      email, display_name: name || null, role_id, role_scope: location_ids.length && role_id ? 'buildings' : 'workspace',
      location_ids: [...new Set(location_ids)], position_ids: [...new Set(position_ids)], job_title: title || null,
    } };
  });
  return { rows, problems };
}
